// Social Links Manager Component
import React, { useState, useEffect } from 'react';
import { Loader2, Save, ExternalLink, Linkedin, Facebook, Instagram, MessageCircle, Youtube, ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// X (Twitter) icon component
const XIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

// Messenger icon component
const MessengerIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.36 2 2 6.13 2 11.7c0 2.91 1.19 5.44 3.14 7.17.16.13.26.35.27.57l.05 1.78c.04.57.61.94 1.13.71l1.98-.87c.17-.08.36-.1.53-.06.91.25 1.87.38 2.9.38 5.64 0 10-4.13 10-9.7C22 6.13 17.64 2 12 2zm5.89 7.73l-2.91 4.6c-.46.73-1.46.92-2.17.42l-2.32-1.73c-.22-.16-.52-.16-.74 0l-3.13 2.38c-.42.32-.97-.18-.69-.62l2.91-4.6c.46-.73 1.46-.92 2.17-.42l2.32 1.73c.22.16.52.16.74 0l3.13-2.38c.42-.32.97.18.69.62z"/>
  </svg>
);

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Platform configurations
const PLATFORMS_CONFIG = {
  linkedin: { name: 'LinkedIn', icon: Linkedin, color: '#0A66C2', placeholder: 'https://linkedin.com/company/votre-entreprise' },
  facebook: { name: 'Facebook', icon: Facebook, color: '#1877F2', placeholder: 'https://facebook.com/votre-page' },
  twitter: { name: 'X (Twitter)', icon: XIcon, color: '#000000', placeholder: 'https://x.com/votre-compte' },
  instagram: { name: 'Instagram', icon: Instagram, color: '#E4405F', placeholder: 'https://instagram.com/votre-compte' },
  whatsapp: { name: 'WhatsApp', icon: MessageCircle, color: '#25D366', placeholder: 'https://wa.me/213XXXXXXXXX' },
  messenger: { name: 'Messenger', icon: MessengerIcon, color: '#0084FF', placeholder: 'https://m.me/votre-page' },
  youtube: { name: 'YouTube', icon: Youtube, color: '#FF0000', placeholder: 'https://youtube.com/@votre-chaine' },
  tiktok: { name: 'TikTok', icon: TikTokIcon, color: '#000000', placeholder: 'https://tiktok.com/@votre-compte' }
};

const DEFAULT_ORDER = ['linkedin', 'facebook', 'twitter', 'instagram', 'whatsapp', 'messenger', 'youtube', 'tiktok'];

const SocialManager = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/social-links`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        // Ensure all platforms have an order
        const settingsWithOrder = {};
        DEFAULT_ORDER.forEach((id, i) => {
          settingsWithOrder[id] = {
            platform: id,
            url: data[id]?.url || '',
            is_active: data[id]?.is_active || false,
            order: data[id]?.order ?? i
          };
        });
        setSettings(settingsWithOrder);
      } else {
        initDefaults();
      }
    } catch (err) {
      console.error('Error fetching social settings:', err);
      initDefaults();
    } finally {
      setLoading(false);
    }
  };

  const initDefaults = () => {
    const defaults = {};
    DEFAULT_ORDER.forEach((id, i) => {
      defaults[id] = { platform: id, url: '', is_active: false, order: i };
    });
    setSettings(defaults);
  };

  // Get sorted platforms by order
  const getSortedPlatforms = () => {
    return Object.entries(settings)
      .sort((a, b) => (a[1].order ?? 0) - (b[1].order ?? 0))
      .map(([id]) => id);
  };

  const handleToggle = (platformId) => {
    setSettings(prev => ({
      ...prev,
      [platformId]: {
        ...prev[platformId],
        platform: platformId,
        is_active: !prev[platformId]?.is_active
      }
    }));
    setHasChanges(true);
  };

  const handleUrlChange = (platformId, url) => {
    setSettings(prev => ({
      ...prev,
      [platformId]: {
        ...prev[platformId],
        platform: platformId,
        url: url
      }
    }));
    setHasChanges(true);
  };

  const handleReorder = (platformId, direction) => {
    const sortedIds = getSortedPlatforms();
    const currentIndex = sortedIds.indexOf(platformId);
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (targetIndex < 0 || targetIndex >= sortedIds.length) return;
    
    const targetPlatformId = sortedIds[targetIndex];
    
    // Swap orders
    const currentOrder = settings[platformId].order;
    const targetOrder = settings[targetPlatformId].order;
    
    setSettings(prev => ({
      ...prev,
      [platformId]: { ...prev[platformId], order: targetOrder },
      [targetPlatformId]: { ...prev[targetPlatformId], order: currentOrder }
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/social-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        setHasChanges(false);
      }
    } catch (err) {
      console.error('Error saving social settings:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const sortedPlatformIds = getSortedPlatforms();

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-light text-slate-800">Réseaux Sociaux</h2>
          <p className="text-sm text-slate-500 mt-1">
            Configurez les liens vers vos réseaux sociaux (affichés dans le footer et la page contact)
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving || !hasChanges}>
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Enregistrer
        </Button>
      </div>

      <div className="space-y-3">
        {sortedPlatformIds.map((platformId, index) => {
          const config = PLATFORMS_CONFIG[platformId];
          if (!config) return null;
          
          const Icon = config.icon;
          const platformSettings = settings[platformId] || { url: '', is_active: false, order: index };
          const isActive = platformSettings.is_active;
          
          return (
            <div 
              key={platformId}
              className={`bg-white rounded-xl border p-4 transition-all ${
                isActive ? 'border-slate-300 shadow-sm' : 'border-slate-200 opacity-75'
              }`}
            >
              <div className="flex items-center gap-3">
                {/* Reorder buttons */}
                <div className="flex flex-col gap-0.5">
                  <button
                    onClick={() => handleReorder(platformId, 'up')}
                    disabled={index === 0}
                    className={`p-1 rounded ${index === 0 ? 'text-slate-200' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'}`}
                    title="Monter"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <span className="text-xs text-slate-400 text-center w-6">{index + 1}</span>
                  <button
                    onClick={() => handleReorder(platformId, 'down')}
                    disabled={index === sortedPlatformIds.length - 1}
                    className={`p-1 rounded ${index === sortedPlatformIds.length - 1 ? 'text-slate-200' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'}`}
                    title="Descendre"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>

                {/* Icon */}
                <div 
                  className="w-11 h-11 rounded-xl flex items-center justify-center transition-colors flex-shrink-0"
                  style={{ 
                    backgroundColor: isActive ? `${config.color}15` : '#f1f5f9',
                    color: isActive ? config.color : '#94a3b8'
                  }}
                >
                  <Icon className="w-5 h-5" />
                </div>
                
                {/* Platform name and toggle */}
                <div className="flex-shrink-0 w-28">
                  <h3 className="font-medium text-slate-800 text-sm">{config.name}</h3>
                  <button
                    onClick={() => handleToggle(platformId)}
                    className={`mt-1 text-xs px-2 py-0.5 rounded-full transition-colors ${
                      isActive 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-slate-100 text-slate-500'
                    }`}
                  >
                    {isActive ? 'Actif' : 'Inactif'}
                  </button>
                </div>
                
                {/* URL input */}
                <div className="flex-1">
                  <input
                    type="url"
                    value={platformSettings.url || ''}
                    onChange={(e) => handleUrlChange(platformId, e.target.value)}
                    placeholder={config.placeholder}
                    className={`w-full px-3 py-2 border rounded-lg text-sm transition-colors ${
                      isActive 
                        ? 'border-slate-300 bg-white' 
                        : 'border-slate-200 bg-slate-50 text-slate-400'
                    }`}
                  />
                </div>
                
                {/* Preview link */}
                {isActive && platformSettings.url && (
                  <a
                    href={platformSettings.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 transition-colors"
                    title="Ouvrir le lien"
                  >
                    <ExternalLink className="w-5 h-5" />
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Preview Section */}
      <div className="mt-8 p-6 bg-slate-800 rounded-xl">
        <h4 className="text-sm font-medium text-slate-400 mb-4">Aperçu (footer & contact)</h4>
        <div className="flex items-center gap-3">
          {sortedPlatformIds
            .filter(id => settings[id]?.is_active && settings[id]?.url)
            .map((platformId) => {
              const config = PLATFORMS_CONFIG[platformId];
              if (!config) return null;
              const Icon = config.icon;
              return (
                <a
                  key={platformId}
                  href={settings[platformId]?.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-slate-700 hover:bg-slate-600 flex items-center justify-center text-white transition-colors"
                >
                  <Icon className="w-5 h-5" />
                </a>
              );
            })}
          {!sortedPlatformIds.some(id => settings[id]?.is_active && settings[id]?.url) && (
            <p className="text-slate-500 text-sm">Activez au moins un réseau social pour voir l'aperçu</p>
          )}
        </div>
      </div>

      {/* Help Box */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
        <h4 className="font-medium text-blue-900 mb-2">Conseils</h4>
        <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
          <li><strong>WhatsApp :</strong> Utilisez le format https://wa.me/213XXXXXXXXX (sans + ni espaces)</li>
          <li><strong>Messenger :</strong> Utilisez https://m.me/NomDeVotrePage</li>
          <li>Utilisez les flèches ↑↓ pour réorganiser l'ordre d'affichage</li>
          <li>Les icônes apparaîtront dans le footer et sur la page Contact</li>
        </ul>
      </div>
    </div>
  );
};

export default SocialManager;
